/**
 * Predecessor Parser — DEV-562
 *
 * Parses, serializes, validates, and renumbers predecessor strings
 * in the extended format:  {taskId}{type}{±lag}
 *
 * Examples:
 *   "3"         → taskId 3, type FS, lag 0  (backward-compatible)
 *   "3FS"       → taskId 3, type FS, lag 0
 *   "5SS+2"     → taskId 5, type SS, lag +2 days
 *   "2FF-1"     → taskId 2, type FF, lag -1 day (lead)
 *   "1SF"       → taskId 1, type SF, lag 0
 *   "1, 3SS+2, 5FF-1"  → multiple predecessors
 *
 * The dependency_type column on the gantt_tasks row is no longer used;
 * each predecessor carries its own type.
 */

/** Supported dependency types */
export type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

export const DEPENDENCY_TYPES: readonly DependencyType[] = ['FS', 'SS', 'FF', 'SF'] as const;

export const DEPENDENCY_TYPE_SET = new Set<string>(DEPENDENCY_TYPES);

/** One parsed predecessor link */
export interface ParsedPredecessor {
  taskId: number;
  type: DependencyType;
  lag: number; // positive = lag (delay), negative = lead (overlap)
}

/**
 * Regex for a single predecessor token.
 * Groups:  1=taskId  2=type (optional)  3=sign+lag (optional)
 *
 * Examples matched:
 *   "3"       → ["3", undefined, undefined]
 *   "3FS"     → ["3", "FS", undefined]
 *   "5SS+2"   → ["5", "SS", "+2"]
 *   "2FF-1"   → ["2", "FF", "-1"]
 */
const PRED_TOKEN_RE = /^(\d+)\s*(FS|SS|FF|SF)?\s*([+-]\d+)?$/i;

/**
 * Parse a single predecessor token string into its components.
 * Returns null if the token is malformed.
 */
export function parsePredecessorToken(token: string): ParsedPredecessor | null {
  const trimmed = token.trim();
  if (!trimmed) return null;

  const match = trimmed.match(PRED_TOKEN_RE);
  if (!match) return null;

  const taskId = parseInt(match[1], 10);
  if (isNaN(taskId) || taskId <= 0) return null;

  const rawType = match[2]?.toUpperCase() ?? 'FS';
  if (!DEPENDENCY_TYPE_SET.has(rawType)) return null;

  const lag = match[3] ? parseInt(match[3], 10) : 0;
  if (isNaN(lag)) return null;

  return { taskId, type: rawType as DependencyType, lag };
}

/**
 * Parse a full predecessor string (comma-separated tokens) into an array.
 * Invalid tokens are silently dropped.
 */
export function parsePredecessors(predecessors: string): ParsedPredecessor[] {
  if (!predecessors || !predecessors.trim()) return [];

  return predecessors
    .split(',')
    .map((tok) => parsePredecessorToken(tok))
    .filter((p): p is ParsedPredecessor => p !== null);
}

/**
 * Serialize a ParsedPredecessor back to string form.
 * Omits type suffix if FS with zero lag (backward compat).
 * Omits lag if zero.
 */
export function serializePredecessor(pred: ParsedPredecessor): string {
  let s = String(pred.taskId);
  // Always include type for non-FS deps; include for FS only if there's a lag
  if (pred.type !== 'FS' || pred.lag !== 0) {
    s += pred.type;
  }
  if (pred.lag > 0) {
    s += `+${pred.lag}`;
  } else if (pred.lag < 0) {
    s += String(pred.lag); // negative sign included
  }
  return s;
}

/**
 * Serialize an array of ParsedPredecessors back to a comma-separated string.
 */
export function serializePredecessors(preds: ParsedPredecessor[]): string {
  return preds.map(serializePredecessor).join(', ');
}

/**
 * Validate a predecessor string. Returns an array of error messages.
 * @param predecessors  The raw predecessor string to validate
 * @param validTaskIds  Set of taskIds that exist in the current project
 * @param ownTaskId     The taskId of the task being validated (to catch self-references)
 */
export function validatePredecessors(
  predecessors: string,
  validTaskIds: Set<number>,
  ownTaskId: number
): string[] {
  if (!predecessors || !predecessors.trim()) return [];

  const errors: string[] = [];
  const tokens = predecessors.split(',').map((t) => t.trim()).filter(Boolean);

  for (const token of tokens) {
    const parsed = parsePredecessorToken(token);
    if (!parsed) {
      errors.push(`Invalid predecessor syntax: "${token}". Expected format: {taskId}[FS|SS|FF|SF][±lag]`);
      continue;
    }
    if (parsed.taskId === ownTaskId) {
      errors.push(`Task cannot reference itself as a predecessor (${token}).`);
      continue;
    }
    if (!validTaskIds.has(parsed.taskId)) {
      errors.push(`Predecessor task ${parsed.taskId} does not exist.`);
    }
  }

  return errors;
}

/**
 * Renumber predecessor references when taskIds change.
 * Preserves dependency type and lag.
 *
 * @param predecessors  The raw predecessor string
 * @param idMap         Map from old taskId → new taskId
 * @returns             Updated predecessor string
 */
export function renumberPredecessors(
  predecessors: string,
  idMap: Map<number, number>
): string {
  if (!predecessors || !predecessors.trim() || idMap.size === 0) return predecessors;

  const parsed = parsePredecessors(predecessors);
  if (parsed.length === 0) return predecessors;

  const updated = parsed.map((p) => ({
    ...p,
    taskId: idMap.get(p.taskId) ?? p.taskId,
  }));

  return serializePredecessors(updated);
}

/**
 * Extract just the taskId numbers from a predecessor string.
 * Useful for building dependency graphs where type/lag are handled separately.
 */
export function extractPredecessorTaskIds(predecessors: string): number[] {
  return parsePredecessors(predecessors).map((p) => p.taskId);
}
